// ProcessCard — shared shell for "lever" cards (the four Manage Account
// actions). Desktop/tablet (≥601px): full card chrome — icon + title +
// question + inline external CTA over the body. Phones (≤600px): collapses to
// an accordion with the CTA full-width at the bottom. Open state is owned by
// the parent so only one is open at a time. From the prototype's ProcessCard.
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import { useMediaQuery } from '../../../hooks';
import { Surface } from '../layout/Surface';
import { ExternalCTA } from '../data-display/ExternalCTA';
import { CaretIcon } from '../../icons/Icons';
import './process-card.css';

export function ProcessCard({
  tone = 'blue',
  icon,
  title,
  question,
  ctaTone = 'blue',
  ctaLabel,
  onCta,
  summary,
  open,
  onToggle,
  className,
  children,
}) {
  const isPhone = useMediaQuery('(max-width: 600px)');

  if (!isPhone) {
    return (
      <Surface as="section" padding="none" className={cx('process-card', className)}>
        <header className="process-card__head">
          <div className="process-card__head-l">
            <span className={cx('process-card__icon', `t-${tone}`)}>{icon}</span>
            <div>
              <h2 className="process-card__title">{title}</h2>
              {question && <p className="process-card__q">{question}</p>}
            </div>
          </div>
          {ctaLabel && (
            <ExternalCTA tone={ctaTone} onClick={onCta}>
              {ctaLabel}
            </ExternalCTA>
          )}
        </header>
        {children}
      </Surface>
    );
  }

  return (
    <Surface as="section" padding="none" className={cx('process-card', 'process-card--acc', open && 'is-open', className)}>
      <button type="button" className="process-card__acc-head" aria-expanded={open} onClick={onToggle}>
        <span className={cx('process-card__icon', `t-${tone}`)}>{icon}</span>
        <span className="process-card__acc-txt">
          <span className="process-card__title">{title}</span>
          {summary && <span className="process-card__summary">{summary}</span>}
        </span>
        <span className="process-card__chev">
          <CaretIcon width={18} height={18} />
        </span>
      </button>
      {open && (
        <div className="process-card__acc-body">
          {question && <p className="process-card__q">{question}</p>}
          {children}
          {ctaLabel && (
            <div className="process-card__acc-cta">
              <ExternalCTA tone={ctaTone} onClick={onCta} fullWidth>
                {ctaLabel}
              </ExternalCTA>
            </div>
          )}
        </div>
      )}
    </Surface>
  );
}

ProcessCard.propTypes = {
  tone: PropTypes.oneOf(['blue', 'green', 'purple', 'orange']),
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  question: PropTypes.node,
  ctaTone: PropTypes.oneOf(['blue', 'green', 'purple', 'orange', 'neutral']),
  ctaLabel: PropTypes.node,
  onCta: PropTypes.func,
  /** Collapsed-state summary shown in the accordion header on phones. */
  summary: PropTypes.node,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default ProcessCard;
